import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { DegreeAudit } from './acad/DegreeAudit';

export const AcademicRecordCard = ({ studentId }) => {
  const qc = useQueryClient();
  const { data: records } = useQuery({ queryKey: ['spr', studentId], queryFn: () => api.get(`/acad/student-programmes/${studentId}`).then((r) => r.data).catch(() => []) });
  const { data: progs } = useQuery({ queryKey: ['acad-progs'], queryFn: () => api.get('/acad/programmes').then((r) => r.data).catch(() => []) });
  const { data: terms } = useQuery({ queryKey: ['acad-terms'], queryFn: () => api.get('/acad/terms').then((r) => r.data).catch(() => []) });
  const { data: staff } = useQuery({ queryKey: ['acad-staff'], queryFn: () => api.get('/acad/staff').then((r) => r.data).catch(() => []) });
  const [f, setF] = useState({ programme_id: '', admit_term_id: '', reason: '' });
  const [openHist, setOpenHist] = useState(null);
  const enrol = async () => {
    try {
      await api.post('/acad/student-programmes', { student_id: studentId, programme_id: f.programme_id, admit_term_id: f.admit_term_id || null, reason: f.reason || 'Enrolled from student record' });
      toast.success('Programme added to the academic record');
      setF({ programme_id: '', admit_term_id: '', reason: '' });
      qc.invalidateQueries({ queryKey: ['spr', studentId] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const change = async (rid, patch, reason) => {
    try {
      await api.post(`/acad/student-programmes/${rid}/change`, { ...patch, reason });
      toast.success('Record updated - change appended to history');
      qc.invalidateQueries({ queryKey: ['spr', studentId] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-4" data-testid="s360-academic-record">
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <p className="font-display font-bold mb-2">Add to a programme</p>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="spr-programme" value={f.programme_id} onChange={(e) => setF({ ...f, programme_id: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
            <option value="">Programme...</option>
            {(progs || []).map((p) => <option key={p.id} value={p.id}>{p.code} {p.name}</option>)}
          </select>
          <select data-testid="spr-term" value={f.admit_term_id} onChange={(e) => setF({ ...f, admit_term_id: e.target.value })} className="border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
            <option value="">Admit term (optional)...</option>
            {(terms || []).map((t) => <option key={t.id} value={t.id}>{t.code}</option>)}
          </select>
          <Button data-testid="spr-add" size="sm" className="bg-slate-900 hover:bg-slate-800" disabled={!f.programme_id} onClick={enrol}>Add programme</Button>
        </div>
        <p className="text-xs text-slate-400 mt-1">A student can hold more than one programme (dual degree, major and minor). Changes are appended, never overwritten.</p>
      </div>
      {(records || []).map((r) => (
        <div key={r.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`spr-row-${r.id}`}>
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div className="text-sm">
              <b>{r.programme_code}</b> {r.programme_name}
              <p className="text-xs text-slate-500">Status: <b>{r.status}</b> · Standing: <b>{r.academic_standing}</b> · Version {r.version}</p>
              <p className="text-xs text-slate-500">Advisor: <b>{(staff || []).find((u) => u.id === r.advisor_user_id)?.name || 'None'}</b></p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <select data-testid={`spr-advisor-${r.id}`} value={r.advisor_user_id || ''} onChange={(e) => e.target.value && change(r.id, { advisor_user_id: e.target.value }, 'Advisor assigned')}
                className="text-xs border border-slate-200 rounded px-1.5 py-1 bg-white">
                <option value="">Set advisor...</option>
                {(staff || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <button data-testid={`spr-hold-${r.id}`} onClick={() => change(r.id, { status: r.status === 'On leave' ? 'Active' : 'On leave' }, r.status === 'On leave' ? 'Returned from leave' : 'Placed on leave')}
                className="text-xs border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">{r.status === 'On leave' ? 'Mark returned' : 'Mark on leave'}</button>
              <button data-testid={`spr-history-${r.id}`} onClick={() => setOpenHist(openHist === r.id ? null : r.id)}
                className="text-xs border border-slate-200 rounded px-2 py-1 hover:bg-slate-50">{openHist === r.id ? 'Hide history' : 'View history'}</button>
            </div>
          </div>
          {openHist === r.id && (
            <div className="mt-3 border-t border-slate-100 pt-2" data-testid={`spr-history-list-${r.id}`}>
              {(r.history || []).slice().reverse().map((h) => (
                <p key={h.at} className="text-xs text-slate-600 py-1">{h.at?.slice(0, 16)} · {h.change} · by {h.by}{h.reason ? ` · ${h.reason}` : ''}</p>
              ))}
            </div>
          )}
        </div>
      ))}
      {(records || []).length === 0 && <p className="text-sm text-slate-400" data-testid="spr-empty">No programme records yet - add one above to start the academic record.</p>}
      {(records || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="s360-degree-audit">
          <p className="font-display font-bold mb-2">Degree audit</p>
          <DegreeAudit studentId={studentId} />
        </div>
      )}
    </div>
  );
};
